import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsAdmin, useIsReseller } from "../../hooks/useIsAdmin";
import {
  NavItem,
  NavList,
  SidebarContainer,
  StyledNavLink,
  NavSection,
  NavSectionHeader,
  NavSectionTitle,
  NavGroup,
  NavGroupLink,
  SidebarContextHeader,
} from "../../components/SidebarComponents";
import { MobileDrawerFooter, MobileDrawerHeader } from "../components/AppNavbar";
import { HelpCenterSidebarLink } from "../help/HelpCenterSidebarLink";
import { useHasCatalogFeature } from "../../hooks/useHasCatalogFeature";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { catalogSidebarLinks } from "./sidebarNav";

interface StoreSidebarProps {
  storeId: string | null;
  onNavigate?: () => void;
}

export const StoreSidebar = ({ storeId, onNavigate }: StoreSidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isReseller = useIsReseller();
  const hasFeature = useHasCatalogFeature((storeId as StoreId) ?? null, false);
  const isCatalogRoute = location.pathname.includes("/catalog");
  const [catalogOpen, setCatalogOpen] = useState(isCatalogRoute);
  const [wasCatalogRoute, setWasCatalogRoute] = useState(isCatalogRoute);

  if (isCatalogRoute !== wasCatalogRoute) {
    setWasCatalogRoute(isCatalogRoute);
    if (isCatalogRoute) {
      setCatalogOpen(true);
    }
  }

  if (!storeId) return null;

  const showGiftcards = hasFeature([Features.GIFT_CARD]);
  const showCreditNotes = hasFeature([Features.CREDIT_NOTE]);
  const showStock = hasFeature([Features.INVENTORY_COSTING]);
  const showAiChat = isAdmin || isReseller;

  const handleClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <SidebarContainer>
      <MobileDrawerHeader onNavigate={handleClick} showStoreSwitcher={false} />
      <NavList>
        <NavSection>
          <SidebarContextHeader storeId={storeId} onNavigate={handleClick} />
          <NavItem>
            <StyledNavLink to="overview" end onClick={handleClick}>
              {t("sidebar.overview")}
            </StyledNavLink>
          </NavItem>
          <NavGroup
            label={t("sidebar.catalog")}
            open={catalogOpen}
            onToggle={() => setCatalogOpen((open) => !open)}
          >
            {catalogSidebarLinks
              .filter((link) => hasFeature(link.features))
              .map((link) => (
                <NavGroupLink key={link.to} to={link.to} onClick={handleClick}>
                  {t(link.label)}
                </NavGroupLink>
              ))}
          </NavGroup>
          <NavItem>
            <StyledNavLink to="statistics" onClick={handleClick}>
              {t("sidebar.statistics")}
            </StyledNavLink>
          </NavItem>
          <NavItem>
            <StyledNavLink to="receipts" onClick={handleClick}>
              {t("sidebar.receipts")}
            </StyledNavLink>
          </NavItem>
          {showAiChat && (
            <NavItem>
              <StyledNavLink to="chat-sessions" onClick={handleClick}>
                {t("sidebar.aiAssistant")}
              </StyledNavLink>
            </NavItem>
          )}
          <NavItem>
            <StyledNavLink to="devices" onClick={handleClick}>
              {t("sidebar.devices")}
            </StyledNavLink>
          </NavItem>
          <NavItem>
            <StyledNavLink to="users" onClick={handleClick}>
              {t("sidebar.users")}
            </StyledNavLink>
          </NavItem>
          {showGiftcards && (
            <NavItem>
              <StyledNavLink to="giftcards" onClick={handleClick}>
                {t("sidebar.giftcards")}
              </StyledNavLink>
            </NavItem>
          )}
          {showCreditNotes && (
            <NavItem>
              <StyledNavLink to="credit-notes" onClick={handleClick}>
                {t("sidebar.creditNotes")}
              </StyledNavLink>
            </NavItem>
          )}
          {showStock && (
            <NavItem>
              <StyledNavLink to="stock" onClick={handleClick}>
                {t("sidebar.stock")}
              </StyledNavLink>
            </NavItem>
          )}
          <NavItem>
            <StyledNavLink to="settings" onClick={handleClick}>
              {t("sidebar.settings")}
            </StyledNavLink>
          </NavItem>
        </NavSection>
        {isAdmin && <NavSection>
          <NavSectionHeader>
            <NavSectionTitle>{t("sidebar.admin")}</NavSectionTitle>
          </NavSectionHeader>
          <NavItem>
            <StyledNavLink to="invoicing" onClick={handleClick}>
              {t("sidebar.invoicing")}
            </StyledNavLink>
          </NavItem>
          <NavItem>
            <StyledNavLink to="integrations" onClick={handleClick}>
              {t("sidebar.integrations")}
            </StyledNavLink>
          </NavItem>
          <NavItem>
            <StyledNavLink to="paymentMethods" onClick={handleClick}>
              {t("sidebar.paymentMethods")}
            </StyledNavLink>
          </NavItem>
        </NavSection>}
      </NavList>
      <HelpCenterSidebarLink />
      <MobileDrawerFooter />
    </SidebarContainer>
  );
};

